import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { faTimes, faTag } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import IconButton from 'components/ui/icon-button/IconButton'
import { ILmsProfileState } from 'store/states/lmsProfile.state'
import { IModeratorPermissionsModel } from 'models/moderatorPermissions.model'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { PermissionsTypes } from 'enum/permissionsType'
import { emptyField } from '../../constants'
import { usePermissionsConditioned } from '../usePermissionsConditioned'
import styles from './PermissionsTableRow.module.scss'

interface IPermissionsTableRow {
  variant: PermissionsTypes
  selectorData: ILmsProfileState | any
  data?: IModeratorPermissionsModel
  setData: any
  index: number
  onDelete?: () => void
}

const PermissionsTableRow: React.FC<IPermissionsTableRow> = ({
  variant,
  index,
  data = emptyField,
  setData,
  selectorData,
  onDelete,
}) => {
  const { t } = useTranslation()
  const prefix = 'configuration.user.permissionsManagementModal'
  const [currentData, setCurrentData] = useState(emptyField)
  const [disabled, setDisabled] = useState(false)
  useEffect(() => {
    setDisabled(
      !!(
        data &&
        data.selected &&
        (data.selected.length === 0 || data.selected.value <= 0)
      )
    )
  }, [data])

  useEffect(() => {
    setCurrentData(data)
  }, [data])

  const setInputData = async (index: number, inputName: string, value: any) => {
    if (inputName !== 'selected' || value !== null) {
      setCurrentData({
        ...currentData,
        [inputName]:
          inputName === 'selected' && variant === 'profile'
            ? value[value.length - 1]
            : value,
      })
      setData({
        index,
        inputName,
        value:
          inputName === 'selected' && variant === 'profile'
            ? value[value.length - 1]
            : value,
      })
    }
  }

  const { editCourse, manageCourses } = usePermissionsConditioned(variant)

  const manageCoursesValue = currentData?.manageCourses
  return (
    <tr className={styles.tableRow} key={`${variant}-${index}`}>
      <td className={'d-flex overflow-hidden justify-content-center'}>
        <div className="w-100">
          <SelectInput
            name={`selected-${variant}-${index}`}
            childTrim={26}
            placeHolder={t(`${prefix}.tabs.columns.${variant}.placeholder`)}
            isMulti={variant === 'profile'}
            value={data && data.selected}
            options={selectorData || []}
            withColors={variant === 'profile'}
            onChange={(data: any) => setInputData(index, 'selected', data)}
            cleareable={false}
            closeMenuOnSelect={true}
          />
        </div>
        <div className="pl-2 pt-1">
          {variant === 'course' &&
          data &&
          data.selected &&
          data.selected.tooltip ? (
            <CommonToolTip
              text={data.selected.tooltip || ''}
              position="right"
              className={styles.tooltip}
            >
              <FontAwesomeIcon icon={faTag} className={styles.tag} />
            </CommonToolTip>
          ) : (
            <div className={styles.offset} />
          )}
        </div>
      </td>
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <CheckBoxRadioButtonInput
            name={`seeMonitor-${variant}-${index}`}
            value={true}
            checked={currentData && currentData.seeMonitor}
            inline={true}
            type={'checkbox'}
            label={''}
            disabled={disabled}
            onChange={() =>
              setInputData(index, 'seeMonitor', !currentData.seeMonitor)
            }
          />
        </div>
      </td>
      {editCourse && (
        <td>
          <div className="d-flex justify-content-center align-items-center">
            <CheckBoxRadioButtonInput
              name={`editCourse-${variant}-${index}`}
              value={true}
              checked={currentData && currentData.editCourse}
              inline={true}
              type={'checkbox'}
              label={' '}
              disabled={disabled}
              onChange={() =>
                setInputData(index, 'editCourse', !currentData.editCourse)
              }
            />
          </div>
        </td>
      )}
      {manageCourses && (
        <td>
          <div className="d-flex justify-content-center align-items-center">
            <CheckBoxRadioButtonInput
              name={`manageCourses-${variant}-${index}`}
              value={true}
              checked={manageCoursesValue}
              inline={true}
              type={'checkbox'}
              label={' '}
              disabled={disabled}
              onChange={() => {
                const newManageCourses = !manageCoursesValue
                setInputData(index, 'manageCourses', newManageCourses)
              }}
            />
          </div>
        </td>
      )}
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <CheckBoxRadioButtonInput
            name={`chat-${variant}-${index}`}
            value={true}
            checked={currentData && currentData.chat}
            inline={true}
            type={'checkbox'}
            label={' '}
            disabled={disabled}
            onChange={() => setInputData(index, 'chat', !currentData.chat)}
          />
        </div>
      </td>
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <CheckBoxRadioButtonInput
            name={`reports-${variant}-${index}`}
            value={true}
            checked={currentData && currentData.downloadReports}
            inline={true}
            type={'checkbox'}
            label={' '}
            disabled={disabled}
            onChange={() =>
              setInputData(
                index,
                'downloadReports',
                !currentData.downloadReports
              )
            }
          />
        </div>
      </td>
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <CheckBoxRadioButtonInput
            name={`approveRequests-${variant}-${index}`}
            value={true}
            checked={currentData && currentData.approveRequests}
            inline={true}
            type={'checkbox'}
            label={' '}
            disabled={disabled}
            onChange={() =>
              setInputData(
                index,
                'approveRequests',
                !currentData.approveRequests
              )
            }
          />
        </div>
      </td>
      <td>
        <div className="d-flex justify-content-center align-items-center">
          <CheckBoxRadioButtonInput
            name={`monitoringAreas-${variant}-${index}`}
            value={true}
            checked={currentData && currentData.monitoringAreas}
            inline={true}
            type={'checkbox'}
            label={' '}
            disabled={disabled}
            onChange={() =>
              setInputData(
                index,
                'monitoringAreas',
                !currentData.monitoringAreas
              )
            }
          />
        </div>
      </td>
      <td className="p-2">
        {!disabled ? (
          <IconButton className="m-0" icon={faTimes} onClick={onDelete} />
        ) : (
          <div style={{ minWidth: 22.95 }}></div>
        )}
      </td>
    </tr>
  )
}

export default PermissionsTableRow
